import { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getRoleLabel, isAdminRole, isTrueAdmin } from "@/lib/roles";
import { bottomNavItems, navItems } from "@/components/feature/navigation";
import SidebarNav from "@/components/feature/SidebarNav";
import RouteContent from "@/components/feature/RouteContent";
import NotificationLayer from "@/components/feature/NotificationLayer";
import AccessDeniedToast from "@/components/feature/AccessDeniedToast";

const LOGO_SRC =
  "https://storage.helloreaddy.io/project_files/b8583382-38f6-4b44-9388-11a95eaee93e/fa07c2a6-a0eb-4342-b4d2-b90375c04059_compressed_LOGO-NEWLEXBABY-1.webp";

/**
 * Khung ứng dụng: menu bên trái + vùng nội dung bên phải.
 *
 * Nguyên tắc tối ưu:
 *  - AppShell KHÔNG theo dõi URL. Việc theo dõi URL chỉ nằm trong RouteContent
 *    và từng NavLink, nên bấm chuyển trang không làm vẽ lại toàn bộ khung.
 *  - Danh sách menu + tên + chức vụ được memo hóa, chỉ đổi khi quyền/tài khoản đổi.
 *  - Thông báo realtime tách sang NotificationLayer, không kéo khung vẽ lại.
 */
export default function AppShell() {
  const { nhanVien, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = isAdminRole(nhanVien?.chuc_vu);
  const isTrueAdminUser = isTrueAdmin(nhanVien?.chuc_vu);

  const visibleItems = useMemo(
    () =>
      navItems.filter(
        (item) =>
          (!item.adminOnly || isAdmin) &&
          (!item.trueAdminOnly || isTrueAdminUser) &&
          (!item.employeeHidden || isAdmin)
      ),
    [isAdmin, isTrueAdminUser]
  );

  const visibleBottomItems = useMemo(
    () =>
      bottomNavItems.filter(
        (item) =>
          (!item.adminOnly || isAdmin) &&
          (!item.trueAdminOnly || isTrueAdminUser) &&
          (!item.employeeHidden || isAdmin)
      ),
    [isAdmin, isTrueAdminUser]
  );

  const userName = nhanVien?.ho_ten || "...";
  const roleLabel = useMemo(
    () => getRoleLabel(nhanVien?.chuc_vu),
    [nhanVien?.chuc_vu]
  );
  const initials = useMemo(
    () =>
      (nhanVien?.ho_ten || "?")
        .split(" ")
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase(),
    [nhanVien?.ho_ten]
  );

  // Ổn định danh tính hàm để memo của SidebarNav thật sự có tác dụng.
  const handleSignOut = useCallback(() => {
    void signOut();
  }, [signOut]);

  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);
  const toggleMobileMenu = useCallback(() => setMobileOpen((v) => !v), []);

  const sidebar = useMemo(
    () => (
      <SidebarNav
        items={visibleItems}
        bottomItems={visibleBottomItems}
        userName={userName}
        roleLabel={roleLabel}
        initials={initials}
        onSignOut={handleSignOut}
        onItemClick={closeMobileMenu}
      />
    ),
    [
      visibleItems,
      visibleBottomItems,
      userName,
      roleLabel,
      initials,
      handleSignOut,
      closeMobileMenu,
    ]
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background-50">
      {/* Desktop sidebar */}
      <aside className="hidden h-full w-64 shrink-0 border-r border-background-200 bg-white lg:block">
        <div className="sticky top-0 h-screen">{sidebar}</div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-background-200 bg-white px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            <img src={LOGO_SRC} alt="NewLexBaby" className="h-8 w-8 object-contain" />
          </div>
          <span className="text-sm font-bold text-foreground-950">NewLexBaby</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleMobileMenu}
            aria-label={mobileOpen ? "Đóng menu" : "Mở menu"}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={closeMobileMenu}
          />
          <div className="absolute left-0 top-0 h-full w-64 bg-white">
            <div className="h-full overflow-y-auto pt-14">{sidebar}</div>
          </div>
        </div>
      )}

      <RouteContent />

      <NotificationLayer enabled={isAdmin} />
      <AccessDeniedToast />
    </div>
  );
}